import React, { useCallback, useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
    LAYER_OBJECT_TYPES,
    LAYER_NAME_MIN,
    LAYER_NAME_MAX,
    validateLayerName,
} from '../../../hooks/useDiscoverLayerWizard';
import './LayerWizardStepBasics.scss';

/**
 * Map object type value to i18n label key used in existing Discover layer config.
 */
const TYPE_LABEL_KEYS = Object.freeze({
    ARTIST: 'discoverPage.panel.artistLabel',
    BAND: 'discoverPage.panel.bandLabel',
    EVENT: 'discoverPage.panel.eventLabel',
    SESSION: 'discoverPage.panel.sessionLabel',
    VENUE: 'discoverPage.panel.venueLabel',
});

/**
 * Wizard Step 1 — Layer basics: name and object type.
 */
export default function LayerWizardStepBasics({
    name,
    objectType,
    onNameChange,
    onObjectTypeChange,
    onValidityChange,
    existingLayerNames = [],
    isEditing = false,
}) {
    const { t } = useTranslation();
    const nameId = useId();
    const typeId = useId();
    const [nameBlurred, setNameBlurred] = useState(false);
    const [typeBlurred, setTypeBlurred] = useState(false);

    const nameError = validateLayerName(name, existingLayerNames);
    const typeError = objectType ? null : 'typeRequired';
    const isValid = nameError === null && typeError === null;

    // Report validity to wizard whenever it changes.
    useEffect(() => {
        onValidityChange(isValid);
    }, [isValid, onValidityChange]);

    const handleNameChange = useCallback((e) => {
        onNameChange(e.target.value);
    }, [onNameChange]);

    const handleTypeChange = useCallback((e) => {
        onObjectTypeChange(e.target.value);
    }, [onObjectTypeChange]);

    const handleNameBlur = useCallback(() => {
        setNameBlurred(true);
    }, []);

    const handleTypeBlur = useCallback(() => {
        setTypeBlurred(true);
    }, []);

    const showNameError = nameBlurred && nameError;
    const showTypeError = typeBlurred && typeError;

    const nameErrorMessage = showNameError
        ? t(`discoverPage.layerWizard.basics.errors.${nameError}`, {
            min: LAYER_NAME_MIN,
            max: LAYER_NAME_MAX,
            defaultValue: nameError,
        })
        : null;

    const typeErrorMessage = showTypeError
        ? t('discoverPage.layerWizard.basics.errors.typeRequired', 'Select an object type.')
        : null;

    return (
        <div className="layer-wizard-basics">
            <div className="layer-wizard-basics__field">
                <label htmlFor={nameId} className="layer-wizard-basics__label">
                    {t('discoverPage.layerWizard.basics.nameLabel', 'Layer name')}
                    <span className="layer-wizard-basics__required" aria-hidden="true">*</span>
                </label>
                <input
                    id={nameId}
                    type="text"
                    className={`layer-wizard-basics__input${showNameError ? ' layer-wizard-basics__input--error' : ''}`}
                    value={name}
                    onChange={handleNameChange}
                    onBlur={handleNameBlur}
                    placeholder={t('discoverPage.layerWizard.basics.namePlaceholder', 'e.g. Jazz events in Brooklyn')}
                    maxLength={LAYER_NAME_MAX}
                    autoComplete="off"
                    aria-required="true"
                    aria-invalid={showNameError ? 'true' : undefined}
                    aria-describedby={showNameError ? `${nameId}-error` : `${nameId}-hint`}
                />
                {showNameError ? (
                    <p id={`${nameId}-error`} className="layer-wizard-basics__error" role="alert">
                        {nameErrorMessage}
                    </p>
                ) : (
                    <p id={`${nameId}-hint`} className="layer-wizard-basics__hint">
                        {t('discoverPage.layerWizard.basics.nameHint', '3-80 characters. Use a clear name you can recognize later.')}
                    </p>
                )}
            </div>

            <div className="layer-wizard-basics__field">
                <label htmlFor={typeId} className="layer-wizard-basics__label">
                    {t('discoverPage.layerWizard.basics.typeLabel', 'Object type')}
                    <span className="layer-wizard-basics__required" aria-hidden="true">*</span>
                </label>
                <select
                    id={typeId}
                    className={`layer-wizard-basics__select${showTypeError ? ' layer-wizard-basics__select--error' : ''}`}
                    value={objectType}
                    onChange={handleTypeChange}
                    onBlur={handleTypeBlur}
                    disabled={isEditing}
                    aria-required="true"
                    aria-invalid={showTypeError ? 'true' : undefined}
                    aria-describedby={showTypeError ? `${typeId}-error` : undefined}
                >
                    <option value="" disabled>
                        {t('discoverPage.layerWizard.basics.typePlaceholder', 'Select object type')}
                    </option>
                    {LAYER_OBJECT_TYPES.map((type) => (
                        <option key={type} value={type}>
                            {t(TYPE_LABEL_KEYS[type], type)}
                        </option>
                    ))}
                </select>
                {showTypeError && (
                    <p id={`${typeId}-error`} className="layer-wizard-basics__error" role="alert">
                        {typeErrorMessage}
                    </p>
                )}
            </div>
        </div>
    );
}

LayerWizardStepBasics.propTypes = {
    name: PropTypes.string.isRequired,
    objectType: PropTypes.string.isRequired,
    onNameChange: PropTypes.func.isRequired,
    onObjectTypeChange: PropTypes.func.isRequired,
    onValidityChange: PropTypes.func.isRequired,
    existingLayerNames: PropTypes.arrayOf(PropTypes.string),
    isEditing: PropTypes.bool,
};
